'use client'

import { CardGrid } from '@/components/v3/atoms/card-grid/card-grid'
import { ListViewToggle } from '@/components/v3/molecules/list-view-toggle/list-view-toggle'
import { Pagination } from '@/components/v3/molecules/pagination/pagination'
import { usePaginationUrl } from '@/hooks/use-pagination-url'
import { useSupplierListView } from '@/hooks/use-supplier-list-view'
import React from 'react'
import {
  CardMobileVariant,
  CardProps,
  SupplierCardType,
} from '../../atoms/card/card'
import s from './suppliers-with-pagination.module.scss'
import { TrackedCard } from './tracked-card'

type Props = {
  cards: Omit<CardProps, 'type'>[]
  type: SupplierCardType
  showPaginationInfo?: boolean
  totalCount: number
  pageCount: number
  googleAd?: React.ReactNode
  pageSize: number
  headingLevel?: 'h2' | 'h3' | 'h4'
  /** Server-read view preference cookie, so the first paint matches. */
  initialView: CardMobileVariant
}

export const SuppliersWithPagination = ({
  cards,
  type,
  showPaginationInfo,
  totalCount,
  googleAd,
  pageCount,
  pageSize,
  headingLevel,
  initialView,
}: Props) => {
  const { page, setPage } = usePaginationUrl()
  const [view, setView] = useSupplierListView(initialView)

  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalCount)

  return (
    <div className={s.wrapper}>
      <ListViewToggle value={view} onChange={setView} />
      <CardGrid>
        {cards.map((card, idx) => {
          const shouldLoadImageEager = idx <= 3
          return (
            <React.Fragment key={card.itemId}>
              {idx === 3 && googleAd ? googleAd : null}
              <TrackedCard
                {...card}
                type={type}
                mobileVariant={view}
                imageLoading={shouldLoadImageEager ? 'eager' : 'lazy'}
                {...(headingLevel && { headingLevel })}
              />
            </React.Fragment>
          )
        })}
      </CardGrid>

      {showPaginationInfo && (
        <p className={s.info}>
          {from}-{to} of {totalCount}
        </p>
      )}

      <Pagination page={page} pageCount={pageCount} onPageChange={setPage} />
    </div>
  )
}
